import { useEffect, useState, useRef, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Button,
    CardActions,
    ClickAwayListener,
    Divider,
    Grid,
    Paper,
    Popper,
    Badge,
    TextField,
    useMediaQuery,
    IconButton,
    Stack
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { format } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';

// project imports
import { APP_RELOAD_APIDATA_FREQ } from 'store/constant';
import { useDispatch, useSelector } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import {
    ApiNotificationsGetCollection,
    ApiNotificationsCountCollection,
    ApiNotificationsMarkAsReadCollection,
    ApiNotificationsDeleteCollection
} from 'store/app/admin/notification';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import SkeletonNotificationList from 'ui-component/skeleton/NotificationList';
import NotificationCard from './NotificationCard';

// project hooks
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import useInterval from 'hooks/useInterval';

// assets
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    const theme = useTheme();
    const intl = useIntl();
    const dispatch = useDispatch();
    const listInnerRef = useRef();
    const anchorRef = useRef(null);
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const { dndMode, notificationChannels = ['internal'] } = useConfig();
    const { user, isLoggedIn } = useAuth();
    const acceptsInternalNotifications = Array.isArray(notificationChannels)
        ? notificationChannels.includes('internal')
        : notificationChannels === 'internal';

    const {
        list: notificationList,
        isLoading: notificationIsLoading,
        limit: notificationLimit,
        count: notificationCount
    } = useSelector((state) => state.notification);

    const [open, setOpen] = useState(false);
    const [viewedStatus, setViewedStatus] = useState('all');
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifPage, setNotifPage] = useState(1);
    const [viewedNotifs, setViewedNotifs] = useState([]);

    const baseGroupFilters = useMemo(
        () => ({
            filterType: 'and',
            filterRows: [
                {
                    field: { fieldName: 'recipients', fieldType: 'simple' },
                    operator: 'like',
                    value: `|${user?.id}|`
                },
                {
                    field: { fieldName: 'createdAt', fieldType: 'simple' },
                    operator: 'between',
                    value: [
                        format(new Date().setHours(0, 0, 0, 0), 'yyyy-MM-dd HH:mm'),
                        format(new Date().setHours(24, 0, 0, 0), 'yyyy-MM-dd HH:mm')
                    ]
                }
            ]
        }),
        [user?.id]
    );

    const prevOpen = useRef(open);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;

        if (!isLoggedIn || dndMode || !acceptsInternalNotifications || !open) return;

        let readFilterIdx;
        let unreadFilterIdx;

        switch (viewedStatus) {
            case 'read':
                readFilterIdx = baseGroupFilters.filterRows?.findIndex(
                    (fr) => fr?.field?.fieldName === 'viewedAt' && fr?.operator === 'isNotNull'
                );
                unreadFilterIdx = baseGroupFilters.filterRows?.findIndex(
                    (fr) => fr?.field?.fieldName === 'viewedAt' && fr?.operator === 'isNull'
                );

                if (readFilterIdx === -1) {
                    baseGroupFilters.filterRows.push({
                        field: { fieldName: 'viewedAt', fieldType: 'simple' },
                        operator: 'isNotNull'
                    });
                }
                if (unreadFilterIdx !== -1) {
                    baseGroupFilters.filterRows.splice(unreadFilterIdx, 1);
                }
                break;
            case 'unread':
                readFilterIdx = baseGroupFilters.filterRows?.findIndex(
                    (fr) => fr?.field?.fieldName === 'viewedAt' && fr?.operator === 'isNotNull'
                );
                unreadFilterIdx = baseGroupFilters.filterRows?.findIndex(
                    (fr) => fr?.field?.fieldName === 'viewedAt' && fr?.operator === 'isNull'
                );

                if (readFilterIdx !== -1) {
                    baseGroupFilters.filterRows.splice(readFilterIdx, 1);
                }
                if (unreadFilterIdx === -1) {
                    baseGroupFilters.filterRows.push({
                        field: { fieldName: 'viewedAt', fieldType: 'simple' },
                        operator: 'isNull'
                    });
                }
                break;
            case 'all':
                readFilterIdx = baseGroupFilters.filterRows?.findIndex(
                    (fr) => fr?.field?.fieldName === 'viewedAt' && fr?.operator === 'isNotNull'
                );
                unreadFilterIdx = baseGroupFilters.filterRows?.findIndex(
                    (fr) => fr?.field?.fieldName === 'viewedAt' && fr?.operator === 'isNull'
                );

                if (readFilterIdx !== -1) {
                    baseGroupFilters.filterRows.splice(readFilterIdx, 1);
                }
                if (unreadFilterIdx !== -1) {
                    baseGroupFilters.filterRows.splice(unreadFilterIdx, 1);
                }
                break;
            default:
            // do nothing
        }

        if (unreadCount > 0) {
            ApiNotificationsGetCollection({
                filters: [baseGroupFilters],
                orderBy: [
                    {
                        field: { fieldName: 'severity', fieldType: 'simple' },
                        direction: 'desc'
                    },
                    {
                        field: { fieldName: 'createdAt', fieldType: 'simple' },
                        direction: 'desc'
                    }
                ],
                page: notifPage,
                limit: notificationLimit,
                errorCallback: (error) =>
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    )
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        acceptsInternalNotifications,
        baseGroupFilters,
        dndMode,
        isLoggedIn,
        notifPage,
        notificationLimit,
        open,
        viewedStatus,
        unreadCount
    ]);

    // Counts unread notifications every 5 minutes or breaks if no user or DND mode is on
    useInterval(
        () =>
            ApiNotificationsCountCollection({
                filters: [baseGroupFilters],
                successCallback: (response) => setUnreadCount(response),
                errorCallback: (error) =>
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    )
            }),
        isLoggedIn && acceptsInternalNotifications && !dndMode ? APP_RELOAD_APIDATA_FREQ : null
    );

    if (dndMode || !acceptsInternalNotifications) {
        return (
            <Box sx={{ ml: 2, mr: 3, [theme.breakpoints.down('md')]: { mr: 2 } }}>
                <IconButton disabled>
                    <Badge
                        badgeContent={dndMode ? 'DND' : 'OFF'}
                        color="default"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                                color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.grey[500]
                            }}
                            ref={anchorRef}
                            aria-haspopup="true"
                            color={theme.palette.grey[50]}
                        >
                            <NotificationsNoneIcon fontSize="small" />
                        </Avatar>
                    </Badge>
                </IconButton>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ ml: 2, mr: 3, [theme.breakpoints.down('md')]: { mr: 2 } }}>
                <Badge badgeContent={unreadCount} max={999} color="secondary" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                            '&[aria-controls="menu-list-grow"],&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                            }
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        onClick={() => setOpen((prevOpen) => !prevOpen)}
                        color="inherit"
                    >
                        <NotificationsNoneIcon fontSize="small" />
                    </Avatar>
                </Badge>
            </Box>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [matchesXs ? 5 : 0, 20] } }] }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener
                        onClickAway={(event) => {
                            if (anchorRef.current && anchorRef.current.contains(event.target)) {
                                return;
                            }

                            setOpen(false);

                            if (viewedNotifs?.length > 0) {
                                ApiNotificationsMarkAsReadCollection({
                                    ids: viewedNotifs,
                                    errorCallback: (error) =>
                                        dispatch(
                                            openSnackbar({
                                                open: true,
                                                message: error || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                                                variant: 'alert',
                                                alert: { color: 'error' }
                                            })
                                        )
                                });
                            }
                        }}
                    >
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column" spacing={2}>
                                            <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 3, px: 2 }}>
                                                <Grid item>
                                                    <Box sx={{ px: 2, pt: 0.25 }}>
                                                        <TextField
                                                            id="notification-viewed-status"
                                                            select
                                                            fullWidth
                                                            size="small"
                                                            value={viewedStatus}
                                                            onChange={(event) => setViewedStatus(event?.target.value)}
                                                            SelectProps={{ native: true }}
                                                        >
                                                            <option key="notifications-all" value="all">
                                                                {intl.formatMessage({ id: 'ui-comp-form-notifications-all' })}
                                                            </option>
                                                            <option key="notifications-read" value="unread">
                                                                {intl.formatMessage({ id: 'ui-comp-form-notifications-unread' })}
                                                            </option>
                                                            <option key="notifications-new" value="read">
                                                                {intl.formatMessage({ id: 'ui-comp-form-notifications-read' })}
                                                            </option>
                                                        </TextField>
                                                    </Box>
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        variant="text"
                                                        sx={{ textTransform: 'none' }}
                                                        size="small"
                                                        disableElevation
                                                        disabled={notificationList?.length === 0}
                                                        onClick={() =>
                                                            ApiNotificationsMarkAsReadCollection({
                                                                ids: notificationList?.map((not) => not.id),
                                                                successCallback: () =>
                                                                    dispatch(
                                                                        openSnackbar({
                                                                            open: true,
                                                                            message: (
                                                                                <FormattedMessage id="ui-comp-snackbar-success-text" />
                                                                            ),
                                                                            variant: 'alert',
                                                                            alert: { color: 'success' }
                                                                        })
                                                                    ),
                                                                errorCallback: (error) =>
                                                                    dispatch(
                                                                        openSnackbar({
                                                                            open: true,
                                                                            message: error || (
                                                                                <FormattedMessage id="ui-comp-snackbar-genericerror-text" />
                                                                            ),
                                                                            variant: 'alert',
                                                                            alert: { color: 'error' }
                                                                        })
                                                                    )
                                                            })
                                                        }
                                                    >
                                                        <FormattedMessage id="ui-comp-button-markallasread" />
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            <Divider sx={{ mt: 1 }} />
                                            <Grid item xs={12}>
                                                <PerfectScrollbar
                                                    style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}
                                                    ref={listInnerRef}
                                                    onScrollY={(container) => {
                                                        const { scrollTop, scrollHeight, clientHeight } = container;
                                                        const compHeight = Math.ceil(scrollTop + clientHeight);

                                                        if (
                                                            scrollTop !== 0 &&
                                                            compHeight !== 0 &&
                                                            (compHeight === scrollHeight || compHeight === scrollHeight - 1) &&
                                                            notificationList.length < notificationCount
                                                        ) {
                                                            setNotifPage((prevPage) => prevPage + 1);
                                                        }
                                                    }}
                                                >
                                                    {notificationIsLoading ? (
                                                        <SkeletonNotificationList />
                                                    ) : (
                                                        <Stack direction="column" spacing={2} sx={{ px: 3, maxWidth: 500 }}>
                                                            {notificationList?.map((notification, rowIndex) => (
                                                                <NotificationCard
                                                                    notification={notification}
                                                                    baseKey="notification"
                                                                    rowIndex={rowIndex}
                                                                    handleOnRead={(readNotId) =>
                                                                        setViewedNotifs([...new Set([...viewedNotifs, readNotId])])
                                                                    }
                                                                />
                                                            ))}
                                                        </Stack>
                                                    )}
                                                </PerfectScrollbar>
                                            </Grid>
                                        </Grid>
                                        <Divider />
                                        <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                                            <Button
                                                variant="text"
                                                sx={{ textTransform: 'none' }}
                                                size="small"
                                                disableElevation
                                                disabled={notificationList?.length === 0}
                                                onClick={() =>
                                                    ApiNotificationsDeleteCollection({
                                                        ids: notificationList?.map((not) => not.id),
                                                        successCallback: () =>
                                                            dispatch(
                                                                openSnackbar({
                                                                    open: true,
                                                                    message: intl.formatMessage({ id: 'ui-comp-snackbar-success-text' }),
                                                                    variant: 'alert',
                                                                    alert: { color: 'success' }
                                                                })
                                                            ),
                                                        errorCallback: (error) =>
                                                            dispatch(
                                                                openSnackbar({
                                                                    open: true,
                                                                    message: error || (
                                                                        <FormattedMessage id="ui-comp-snackbar-genericerror-text" />
                                                                    ),
                                                                    variant: 'alert',
                                                                    alert: { color: 'error' }
                                                                })
                                                            )
                                                    })
                                                }
                                            >
                                                <FormattedMessage id="ui-comp-button-removeall" />
                                            </Button>
                                        </CardActions>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
